'use client';

import { ComposerPrimitive, useThreadRuntime } from '@assistant-ui/react';
import { Send, Loader2, Image as ImageIcon, Video, X, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRef, useState, useCallback } from 'react';
import { useComposerContext } from './composer-context';
import { cn } from '@/lib/utils';

interface ComposerProps {
  placeholder?: string;
}

/**
 * Custom Composer Component with toolbar below input (Claude-style)
 * - ⌘/Ctrl + Enter: Send message
 * - Shift + Enter: New line
 * - Esc: Clear input
 * - 📷 Image upload: Click button or drag-and-drop images for GPT-5.2 vision
 * - 🎥 Video upload: Opens MUX upload panel
 * - 🖱️ Drag & Drop: Drop images anywhere on composer to attach
 */
export function Composer({ placeholder = "Type your message..." }: ComposerProps) {
  const threadRuntime = useThreadRuntime();
  const isRunning = threadRuntime.getState().isRunning;
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const {
    pendingAttachments,
    onImageSelect,
    onRemoveAttachment,
    onVideoUpload
  } = useComposerContext();

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter(file =>
        file.type.startsWith('image/')
      );
      if (imageFiles.length > 0) {
        const dataTransfer = new DataTransfer();
        imageFiles.forEach(file => dataTransfer.items.add(file));
        onImageSelect(dataTransfer.files);
      }
    }
  }, [onImageSelect]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Regular Enter submits (assistant-ui default behavior)
    // Shift + Enter creates new line (assistant-ui default behavior)

    // ⌘/Ctrl + Enter also submits (for users who expect it)
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }

    // Esc to clear
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.value = '';
    }
  };

  return (
    <ComposerPrimitive.Root className="border-t bg-background/95 backdrop-blur-sm p-3 shadow-lg">
      <div
        className="max-w-4xl mx-auto relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drop overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-10 rounded-xl border-2 border-dashed border-accent-blue-500 bg-accent-blue-500/10 flex items-center justify-center pointer-events-none">
            <div className="flex flex-col items-center gap-2 text-accent-blue-600 dark:text-accent-blue-400">
              <Upload className="h-8 w-8" />
              <span className="text-sm font-medium">Drop images here</span>
            </div>
          </div>
        )}

        {/* Pending Attachments Preview - above input */}
        {pendingAttachments.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {pendingAttachments.map(attachment => (
              <div key={attachment.id} className="relative group">
                {attachment.type === 'image' && (
                  <div className="relative w-16 h-16 rounded-lg border overflow-hidden bg-muted">
                    {attachment.url ? (
                      <img
                        src={attachment.url}
                        alt={attachment.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    {attachment.status === 'uploading' && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      </div>
                    )}
                    {attachment.status === 'error' && (
                      <div className="absolute inset-0 bg-red-500/50 flex items-center justify-center">
                        <X className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => onRemoveAttachment(attachment.id)}
                  className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 text-xs hidden group-hover:flex items-center justify-center transition-colors"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Clean input area - no overlapping buttons */}
        <div className="rounded-xl border border-input bg-background overflow-hidden focus-within:ring-1 focus-within:ring-accent-blue-500/30 focus-within:border-accent-blue-400 transition-all duration-200">
          <ComposerPrimitive.Input
            className="
              w-full
              min-h-[80px]
              max-h-[320px]
              resize-none
              bg-transparent
              px-4
              py-3
              text-base
              leading-relaxed
              placeholder:text-muted-foreground
              focus:outline-none
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            disabled={isRunning}
            rows={3}
            autoFocus
          />

          {/* Toolbar below input */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-input/50 bg-muted/30">
            {/* Left side: Attachment buttons */}
            <div className="flex items-center gap-1">
              {/* Hidden file input for images */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    onImageSelect(e.target.files);
                    e.target.value = '';
                  }
                }}
              />

              {/* Image Upload Button */}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-2 gap-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                onClick={() => imageInputRef.current?.click()}
                disabled={isRunning}
                title="Upload image (JPG, PNG, WebP, GIF - max 20MB)"
              >
                <ImageIcon className="h-4 w-4" />
                <span className="text-xs hidden sm:inline">Image</span>
              </Button>

              {/* Video Upload Button */}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-2 gap-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                onClick={onVideoUpload}
                disabled={isRunning}
                title="Upload video"
              >
                <Video className="h-4 w-4" />
                <span className="text-xs hidden sm:inline">Video</span>
              </Button>
            </div>

            {/* Right side: Keyboard hints + Send button */}
            <div className="flex items-center gap-3">
              {/* Keyboard hints - hidden on small screens */}
              <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
                <kbd className="px-1.5 py-0.5 text-[10px] bg-background border rounded font-mono">⌘</kbd>
                <span>Enter to send</span>
              </div>

              {/* Send button */}
              <ComposerPrimitive.Send asChild>
                <Button
                  type="submit"
                  size="sm"
                  className="
                    h-8
                    px-3
                    gap-1.5
                    rounded-lg
                    bg-accent-blue-500
                    hover:bg-accent-blue-600
                    hover:scale-105
                    active:scale-95
                    focus:ring-2
                    focus:ring-accent-blue-500
                    focus:ring-offset-2
                    shadow-sm
                    transition-all
                    duration-150
                    disabled:opacity-30
                    disabled:cursor-not-allowed
                    disabled:hover:scale-100
                  "
                  disabled={isRunning}
                >
                  {isRunning ? (
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 text-white" />
                      <span className="text-white text-xs hidden sm:inline">Send</span>
                    </>
                  )}
                </Button>
              </ComposerPrimitive.Send>
            </div>
          </div>
        </div>
      </div>
    </ComposerPrimitive.Root>
  );
}
